import React from 'react';
import PropTypes from 'prop-types';

import { Divider } from '@mui/material';

import Description from 'ui/text/Description';
import Label from 'ui/text/Label';

import { currencySymbol, convertPriceFromCents } from 'utils/rewards';
import formattedDate from 'utils/formattedDate';
import { rewardType } from 'utils/constants';

import { t } from 'i18n/index';

const rewardCategories = Object.values(rewardType).map((r) => r.value);

const OrderSummary = ({ items, frequency }) => {
  const currency = items[0]?.product?.organization?.currency;
  const currencySign = currencySymbol[currency];
  const formatWithCurrency = (value) => `${convertPriceFromCents(value)} ${currencySign}`;

  let totalItemPrice = 0;
  let totalFee = 0;
  let totalNetDiscount = 0;
  let totalEffective = 0;
  let hasDiscount = false;

  items.forEach(({ product, amount }) => {
    const unitPrice = product.unit_price || 0;
    const fee = product.application_fee || 0;
    const netDiscount = product.net_discount || 0;
    const discountPrice = product.discount_price;

    totalItemPrice += unitPrice * amount;
    totalFee += fee * amount;
    totalNetDiscount += netDiscount * amount;
    totalEffective += (discountPrice != null ? discountPrice : unitPrice) * amount;
    if (discountPrice != null) hasDiscount = true;
  });

  const showFeeRow = totalFee > 0;
  const showDiscountRow = hasDiscount && totalNetDiscount > 0;
  const totalDue = totalEffective + totalFee;

  const firstCategory = items[0]?.product?.category;
  const isReward = rewardCategories.includes(firstCategory);
  const subtotalLabel = frequency && isReward
    ? t(`profile:memberships:claimModal:summary:${frequency}`)
    : t('literal:subtotal');

  return (
    <div className='summary'>
      <Description bold large>{t('literal:orderSummary')}</Description>

      {/* SELL06 — a cart can hold several SKUs of one product, so the label needs
          the variant and the key needs its id: on product.id alone two variants
          of the same product collide. */}
      {items.map(({ product, amount, session_start_date, variant }) => {
        const unitPrice = product.unit_price || 0;
        const sessionDate = session_start_date ?? product.session_start_date;

        return (
          <div key={variant ? `${product.id}-${variant.id}` : product.id} className='summary-row'>
            <div className='summary-column'>
              <Description>{product.title}{variant && ` — ${variant.label}`}</Description>
              {sessionDate && <Label secondary>{formattedDate(sessionDate, 'datetime')}</Label>}
            </div>
            {/* Dynamic text stays wrapped in elements: Google Translate splits
                bare text nodes, and React inserting the "N x " prefix next to a
                translated node crashes with insertBefore (Sentry MEMBRZ-WEB-5). */}
            <Description className="cart-price">
              {amount > 1 && <span>{`${amount} x `}</span>}
              <span>{formatWithCurrency(unitPrice)}</span>
            </Description>
          </div>
        );
      })}

      <Divider sx={{ borderColor: 'var(--borderColor)' }} />

      <div className='summary-row'>
        <Description>{subtotalLabel}</Description>
        <Description className="cart-price">{formatWithCurrency(totalItemPrice)}</Description>
      </div>

      {showDiscountRow && (
        <div className='summary-row'>
          <Description>{t('literal:discount')}</Description>
          <Description>- {formatWithCurrency(totalNetDiscount)}</Description>
        </div>
      )}

      {showFeeRow && (
        <div className='summary-row'>
          <Description>{t('literal:applicationFee')}</Description>
          <Description className="cart-price">{formatWithCurrency(totalFee)}</Description>
        </div>
      )}

      <Divider sx={{ borderColor: 'var(--borderColor)' }} />

      <div className='summary-row'>
        <Description bold>{t('profile:memberships:claimModal:totalDue')}</Description>
        <Description bold className="cart-price">{formatWithCurrency(totalDue)}</Description>
      </div>
    </div>
  );
};

OrderSummary.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    product: PropTypes.object.isRequired,
    amount: PropTypes.number.isRequired,
    session_start_date: PropTypes.string,
    variant: PropTypes.object,
  })).isRequired,
  frequency: PropTypes.string,
};

export default OrderSummary;
